import React, {FC, useEffect, useMemo, useRef, useState} from 'react'
import {toast} from 'react-toastify'
import {useAuth} from '../../auth'
import {getCurrencies, type CurrencyOption} from '../../Management/core/_requests'
import {
  createDirectPurchaseOrder,
  DirectPurchaseOrderFiles,
  getAccountingAccounts,
  getAllInventoryCategoriesForCompany,
  getAllVessels,
  getCompanyGroupAdmins,
  getEquipmentList,
  getInventoryItemSubCategories,
  resolveCurrencyExchangeRate,
  getPartsBySubcomponent,
  getPorts,
  getSubAccounts,
  getUnitsOfMeasure,
  getSubComponentsByEquipment,
  getVendors,
  getVesselsByCompany,
  previewNextPurchaseOrderNumber,
  getVesselInventoryProjection,
  updateDirectPurchaseOrder,
} from '../core/_requests'
import {
  AccountingAccount,
  CompanyGroupAdmin,
  Equipment,
  InventoryCategory,
  InventoryItemHead,
  InventoryItemSubHead,
  InventoryItemSubCategory,
  Part,
  Port,
  PurchaseOrderMutationResponse,
  PurchaseOrder,
  SubComponent,
  SubAccount,
  UnitOfMeasure,
  Vessel,
  VesselInventoryProjection,
  Vendor,
} from '../core/_models'
import {fetchEquipmentOptionsForVessel, type EquipmentLoadStatus} from '../core/equipmentOptions'
import {procurementTenantCgaId} from '../core/procurementTenantContext'
import {loadScopedInventoryHeads, loadScopedInventorySubHeads} from './procurementServiceHierarchy'
import {
  AttachmentSection,
  BasicDetailsSection,
  CommercialSection,
  LineItemsSection,
  ProcurementContextSection,
} from './DirectPurchaseOrderSections'
import {
  buildDirectPurchaseOrderPayload,
  createEmptyDirectPoLine,
  deriveDirectPoCategoryKind,
  deriveRequestForFromCategoryKind,
  lineHasMeaningfulData,
  resetLineForCategoryKind,
  type DirectPoCategoryKind,
  type DirectPoFormDraft,
  type DirectPoLineDraft,
} from './directPurchaseOrderForm'

type DirectPoModalMode = 'create' | 'edit'
type LoadStatus = 'idle' | 'loading' | 'loaded' | 'empty' | 'error'

interface DirectPurchaseOrderModalProps {
  visible: boolean
  mode?: DirectPoModalMode
  purchaseOrder?: PurchaseOrder | null
  onClose: () => void
  onSuccess: (purchaseOrder: PurchaseOrderMutationResponse) => void | Promise<void>
}

const TODAY = '2026-08-13'

const createInitialForm = (currentUser?: any): DirectPoFormDraft => ({
  title: '',
  description: '',
  requisitionDescription: '',
  rfqTitle: '',
  rfqItemDescription: '',
  technicalSpecifications: '',
  requisitionType: 'MEDIUM',
  requestFor: 'TECHNICAL',
  vesselId: '',
  categoryId: '',
  requestedPortId: '',
  deliveryPortId: '',
  requiredDeliveryDate: '',
  responseDueDate: '',
  promisedDeliveryDate: '',
  vendorId: '',
  vendorCompanyMappingId: '',
  poDate: TODAY,
  supplierReference: '',
  logisticAccountCode: '',
  invoiceAddressedTo: '',
  poRaisedByCompany:
    String(
      currentUser?.companyGroupAdmin?.name || currentUser?.vessel?.companyGroupAdmin?.name || ''
    ) || '',
  poRaisedByUser: String(currentUser?.username || ''),
  accountCategory: '',
  deliveryDate: '',
  deliveryTerms: '',
  paymentTerms: '',
  termsOfPayment: '',
  terms: '',
  specialInstructions: '',
  remittanceInstructions: '',
  currency: 'USD',
  quotationCurrency:
    String(
      currentUser?.companyGroupAdmin?.primaryCurrencyCode ||
        currentUser?.primaryCurrencyCode ||
        'USD'
    ) || 'USD',
  companyCurrencyCode:
    String(
      currentUser?.companyGroupAdmin?.primaryCurrencyCode ||
        currentUser?.primaryCurrencyCode ||
        'USD'
    ) || 'USD',
  exchangeRate: '',
  exchangeRateDate: '',
  exchangeRateDirect: false,
  totalDiscount: '',
  freightCost: '',
  packagingCost: '',
  truckCost: '',
  bargeCost: '',
  otherCost: '',
  vatOnLogistics: '',
})

const normalizeText = (value?: string | null) => String(value || '').trim()
const toPositiveNumber = (value: unknown): number | null => {
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

const getCategoryNameById = (categories: InventoryCategory[], categoryId: string) =>
  categories.find((category) => String(category.id) === String(categoryId))?.name || ''

const DirectPurchaseOrderModal: FC<DirectPurchaseOrderModalProps> = ({
  visible,
  mode = 'create',
  purchaseOrder,
  onClose,
  onSuccess,
}) => {
  const {currentUser, isSuperAdmin} = useAuth()
  const [form, setForm] = useState<DirectPoFormDraft>(() => createInitialForm(currentUser))
  const [lines, setLines] = useState<DirectPoLineDraft[]>([createEmptyDirectPoLine()])
  const [companies, setCompanies] = useState<CompanyGroupAdmin[]>([])
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null)
  const [selectedCompanyUsername, setSelectedCompanyUsername] = useState('')
  const [vessels, setVessels] = useState<Vessel[]>([])
  const [vendors, setVendors] = useState<Vendor[]>([])
  const vendorRequestSequence = useRef(0)
  const lastVendorCompanyId = useRef<number | null>(null)
  const [ports, setPorts] = useState<Port[]>([])
  const [categories, setCategories] = useState<InventoryCategory[]>([])
  const [subCategories, setSubCategories] = useState<InventoryItemSubCategory[]>([])
  const [accounts, setAccounts] = useState<AccountingAccount[]>([])
  const [accountsLoading, setAccountsLoading] = useState(false)
  const accountRequestSequence = useRef(0)
  const [uoms, setUoms] = useState<UnitOfMeasure[]>([])
  const [currencies, setCurrencies] = useState<CurrencyOption[]>([])
  const [subaccountsByLine, setSubaccountsByLine] = useState<Record<number, SubAccount[]>>({})
  const [budgetSubaccountsByLine, setBudgetSubaccountsByLine] = useState<
    Record<number, SubAccount[]>
  >({})
  const subaccountRequestSequence = useRef<Record<string, number>>({})
  const [storeOptionsByLine, setStoreOptionsByLine] = useState<
    Record<number, VesselInventoryProjection[]>
  >({})
  const [equipmentOptions, setEquipmentOptions] = useState<Equipment[]>([])
  const [equipmentStatus, setEquipmentStatus] = useState<EquipmentLoadStatus>('idle')
  const [equipmentMessage, setEquipmentMessage] = useState<string | null>(null)
  const [subcomponentsByLine, setSubcomponentsByLine] = useState<Record<number, SubComponent[]>>({})
  const [subcomponentStatusByLine, setSubcomponentStatusByLine] = useState<
    Record<number, LoadStatus>
  >({})
  const [subcomponentMessageByLine, setSubcomponentMessageByLine] = useState<
    Record<number, string | null>
  >({})
  const [partsByLine, setPartsByLine] = useState<Record<number, Part[]>>({})
  const [partStatusByLine, setPartStatusByLine] = useState<Record<number, LoadStatus>>({})
  const [partMessageByLine, setPartMessageByLine] = useState<Record<number, string | null>>({})
  const [serviceHeads, setServiceHeads] = useState<InventoryItemHead[]>([])
  const [serviceSubheadsByLine, setServiceSubheadsByLine] = useState<
    Record<number, InventoryItemSubHead[]>
  >({})
  const [requisitionFiles, setRequisitionFiles] = useState<File[]>([])
  const [rfqFiles, setRfqFiles] = useState<File[]>([])
  const [quotationFiles, setQuotationFiles] = useState<File[]>([])
  const [poFiles, setPoFiles] = useState<File[]>([])
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [poNumberPreview, setPoNumberPreview] = useState<string | null>(null)
  const [poNumberPreviewStatus, setPoNumberPreviewStatus] = useState<'idle' | 'loading' | 'unavailable'>('idle')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const tenantCgaId = useMemo(() => procurementTenantCgaId(currentUser), [currentUser])
  const currentUserSignature = useMemo(
    () =>
      JSON.stringify({
        username: currentUser?.username || '',
        companyGroupAdminId:
          currentUser?.companyGroupAdminId || currentUser?.companyGroupAdmin?.id || '',
        companyGroupAdminUsername: currentUser?.companyGroupAdmin?.username || '',
        companyGroupAdminName: currentUser?.companyGroupAdmin?.name || '',
        vesselCompanyGroupAdminName: currentUser?.vessel?.companyGroupAdmin?.name || '',
        primaryCurrencyCode:
          currentUser?.companyGroupAdmin?.primaryCurrencyCode ||
          currentUser?.primaryCurrencyCode ||
          '',
      }),
    [currentUser]
  )
  const currentCompanyUsername = String(currentUser?.companyGroupAdmin?.username || '').trim()
  const currentCompanyGroupAdminId =
    toPositiveNumber(currentUser?.companyGroupAdminId) ??
    toPositiveNumber(currentUser?.companyGroupAdmin?.id) ??
    toPositiveNumber(currentUser?.companyAdmin?.cgaid?.id)
  const inventoryScopeAll = isSuperAdmin
  const canSelectScopedVessel = !inventoryScopeAll
  const selectedCompanyName = useMemo(
    () =>
      companies.find((company) => company.id === selectedCompanyId)?.name ||
      (purchaseOrder?.companyGroupAdmin?.id === selectedCompanyId
        ? purchaseOrder.companyGroupAdmin.name || ''
        : ''),
    [companies, selectedCompanyId, purchaseOrder]
  )
  const selectedVessel = useMemo(
    () => vessels.find((row) => String(row.id) === String(form.vesselId)),
    [vessels, form.vesselId]
  )
  const effectiveCompanyId = useMemo(
    () =>
      toPositiveNumber(
        selectedVessel?.companyGroupAdminId ??
          selectedVessel?.companyGroupAdmin?.id ??
          (isSuperAdmin ? selectedCompanyId : tenantCgaId ?? currentCompanyGroupAdminId)
      ),
    [isSuperAdmin, selectedVessel, selectedCompanyId, tenantCgaId, currentCompanyGroupAdminId]
  )

  const selectedCategoryName = useMemo(
    () => getCategoryNameById(categories, form.categoryId),
    [categories, form.categoryId]
  )
  const categoryKind = useMemo(
    () => deriveDirectPoCategoryKind(selectedCategoryName),
    [selectedCategoryName]
  )
  const derivedRequestFor = useMemo(
    () => deriveRequestForFromCategoryKind(categoryKind),
    [categoryKind]
  )
  const showLineSection = Boolean(
    form.vesselId && form.vendorId && form.requestedPortId && categoryKind !== 'NONE'
  )
  const showCommercialSection = showLineSection
  const currencyConversionRelevant =
    normalizeText(form.quotationCurrency) !== '' &&
    normalizeText(form.companyCurrencyCode) !== '' &&
    normalizeText(form.quotationCurrency).toUpperCase() !==
      normalizeText(form.companyCurrencyCode).toUpperCase()
  const hasLineData = lines.some((line) => lineHasMeaningfulData(line))

  useEffect(() => {
    if (!visible || mode !== 'create' || !form.vesselId || !form.vendorId) {
      setPoNumberPreview(null)
      setPoNumberPreviewStatus('idle')
      return
    }
    if (typeof previewNextPurchaseOrderNumber !== 'function') {
      setPoNumberPreview(null)
      setPoNumberPreviewStatus('unavailable')
      return
    }
    let active = true
    setPoNumberPreviewStatus('loading')
    void previewNextPurchaseOrderNumber({
      vesselId: Number(form.vesselId),
      vendorId: Number(form.vendorId),
      poDate: form.poDate || undefined,
    }).then((preview) => {
      if (!active) return
      setPoNumberPreview(preview.poNumber)
      setPoNumberPreviewStatus('idle')
    }).catch(() => {
      if (!active) return
      setPoNumberPreview(null)
      setPoNumberPreviewStatus('unavailable')
    })
    return () => { active = false }
  }, [visible, mode, form.vesselId, form.vendorId, form.poDate])

  useEffect(() => {
    if (!visible) return
    let active = true
    const currencyRequest =
      typeof getCurrencies === 'function' ? getCurrencies() : Promise.resolve<CurrencyOption[]>([])
    const subCategoryRequest =
      typeof getInventoryItemSubCategories === 'function'
        ? getInventoryItemSubCategories(true)
        : Promise.resolve<InventoryItemSubCategory[]>([])
    Promise.all([
      getPorts(),
      inventoryScopeAll ? getCompanyGroupAdmins() : Promise.resolve([]),
      currencyRequest,
      subCategoryRequest,
    ])
      .then(([portRows, companyRows, currencyRows, subCategoryRows]) => {
        if (!active) return
        setPorts(portRows || [])
        setCompanies(companyRows || [])
        setCurrencies(currencyRows || [])
        setSubCategories(subCategoryRows || [])
      })
      .catch((error: any) => {
        if (active) toast.error(error?.message || 'Failed to load Direct PO form data')
      })
    return () => {
      active = false
    }
  }, [visible, inventoryScopeAll])

  useEffect(() => {
    const requestSequence = ++vendorRequestSequence.current
    if (!visible) {
      lastVendorCompanyId.current = null
      setVendors([])
      return
    }
    if (!effectiveCompanyId) {
      if (lastVendorCompanyId.current !== null) {
        lastVendorCompanyId.current = null
        setVendors([])
      }
      return
    }
    let active = true
    setLoading(true)
    getVendors(0, 200, effectiveCompanyId)
      .then((vendorPage) => {
        if (active && requestSequence === vendorRequestSequence.current) {
          lastVendorCompanyId.current = effectiveCompanyId
          setVendors(vendorPage.content || [])
        }
      })
      .catch((error: any) => {
        if (active && requestSequence === vendorRequestSequence.current) {
          lastVendorCompanyId.current = effectiveCompanyId
          setVendors([])
          toast.error(error?.message || 'Failed to load vendors')
        }
      })
      .finally(() => {
        if (active && requestSequence === vendorRequestSequence.current) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [visible, effectiveCompanyId])

  useEffect(() => {
    if (!visible) return
    if (typeof getUnitsOfMeasure !== 'function') return
    getUnitsOfMeasure(true)
      .then((rows) => setUoms(rows || []))
      .catch(() => setUoms([]))
  }, [visible])

  useEffect(() => {
    if (!visible || !effectiveCompanyId || purchaseOrder) return
    const companyCurrency =
      (selectedVessel?.companyGroupAdmin as any)?.primaryCurrencyCode ||
      (companies.find((company) => company.id === effectiveCompanyId) as any)
        ?.primaryCurrencyCode ||
      currentUser?.companyGroupAdmin?.primaryCurrencyCode ||
      currentUser?.primaryCurrencyCode
    if (!companyCurrency) return
    setForm((prev) => ({...prev, companyCurrencyCode: String(companyCurrency).toUpperCase()}))
  }, [visible, effectiveCompanyId, selectedVessel, companies, currentUser, purchaseOrder])

  useEffect(() => {
    if (!visible || !effectiveCompanyId || !form.quotationCurrency || !form.companyCurrencyCode) return
    const sourceCurrencyCode = String(form.quotationCurrency).toUpperCase()
    const targetCurrencyCode = String(form.companyCurrencyCode).toUpperCase()
    if (sourceCurrencyCode === targetCurrencyCode) {
      setForm((prev) => ({
        ...prev,
        exchangeRate: '1',
        exchangeRateDate: prev.poDate || TODAY,
        exchangeRateDirect: true,
      }))
      return
    }
    let active = true
    void resolveCurrencyExchangeRate({
      sourceCurrencyCode,
      targetCurrencyCode,
      effectiveAt: form.poDate || undefined,
      companyGroupAdminId: Number(effectiveCompanyId),
      vendorId: form.vendorId ? Number(form.vendorId) : undefined,
    }).then((result) => {
      if (!active) return
      setForm((prev) => ({
        ...prev,
        exchangeRate: result.available && result.exchangeRate != null ? String(result.exchangeRate) : '',
        exchangeRateDate: result.effectiveAt || prev.poDate || TODAY,
        exchangeRateDirect: Boolean(result.direct),
      }))
    }).catch(() => {
      if (!active) return
      setForm((prev) => ({...prev, exchangeRate: '', exchangeRateDate: prev.poDate || TODAY, exchangeRateDirect: false}))
    })
    return () => { active = false }
  }, [visible, effectiveCompanyId, form.quotationCurrency, form.companyCurrencyCode, form.poDate, form.vendorId])

  useEffect(() => {
    if (!visible) return
    setForm(createInitialForm(currentUser))
    setLines([createEmptyDirectPoLine()])
    setErrors({})
    setRequisitionFiles([])
    setRfqFiles([])
    setQuotationFiles([])
    setPoFiles([])
    setSelectedCompanyId(inventoryScopeAll ? null : tenantCgaId ?? null)
    setSelectedCompanyUsername('')
    setAccounts([])
    setSubaccountsByLine({})
    setBudgetSubaccountsByLine({})
    setStoreOptionsByLine({})
    setEquipmentOptions([])
    setEquipmentStatus('idle')
    setEquipmentMessage(null)
    setSubcomponentsByLine({})
    setSubcomponentStatusByLine({})
    setSubcomponentMessageByLine({})
    setPartsByLine({})
    setPartStatusByLine({})
    setPartMessageByLine({})
    setServiceHeads([])
    setServiceSubheadsByLine({})
  }, [visible, currentUserSignature, purchaseOrder?.id, mode, inventoryScopeAll, tenantCgaId])

  useEffect(() => {
    if (!visible || !purchaseOrder) return
    const requisition = purchaseOrder.rfq?.requisition
    const quotationLines = Array.isArray(purchaseOrder.quotationLines)
      ? purchaseOrder.quotationLines
      : []
    const derivedKind = deriveDirectPoCategoryKind(purchaseOrder.category?.name || '')
    setForm({
      ...createInitialForm(currentUser),
      title: requisition?.title || purchaseOrder.description || '',
      description: purchaseOrder.description || '',
      requisitionDescription: requisition?.description || '',
      rfqTitle: purchaseOrder.rfq?.title || '',
      rfqItemDescription: '',
      technicalSpecifications: '',
      requisitionType: requisition?.type || 'MEDIUM',
      requestFor:
        requisition?.requestFor === 'GENERAL_STORES'
          ? 'GENERAL_STORES'
          : deriveRequestForFromCategoryKind(derivedKind),
      vesselId: requisition?.vesselId ? String(requisition.vesselId) : '',
      categoryId: purchaseOrder.category?.id ? String(purchaseOrder.category.id) : '',
      requestedPortId: requisition?.requestedPortId ? String(requisition.requestedPortId) : '',
      deliveryPortId: purchaseOrder.deliveryPortId ? String(purchaseOrder.deliveryPortId) : '',
      requiredDeliveryDate: requisition?.requiredDeliveryDate || '',
      responseDueDate: '',
      promisedDeliveryDate: '',
      vendorId: purchaseOrder.vendorId ? String(purchaseOrder.vendorId) : '',
      vendorCompanyMappingId:
        purchaseOrder.vendorCompanyMappingId != null
          ? String(purchaseOrder.vendorCompanyMappingId)
          : '',
      poDate: purchaseOrder.poDate || TODAY,
      supplierReference: purchaseOrder.supplierReference || '',
      logisticAccountCode: purchaseOrder.logisticAccountCode || '',
      invoiceAddressedTo: purchaseOrder.invoiceAddressedTo || '',
      poRaisedByCompany:
        purchaseOrder.poRaisedByCompany ||
        String(
          (purchaseOrder as any)?.companyGroupAdmin?.name ||
            (purchaseOrder as any)?.rfq?.requisition?.category?.cgaid?.name ||
            ''
        ),
      poRaisedByUser: purchaseOrder.poRaisedByUser || String(currentUser?.username || ''),
      accountCategory: purchaseOrder.accountCategory || '',
      deliveryDate: purchaseOrder.deliveryDate || '',
      deliveryTerms: purchaseOrder.deliveryTerms || purchaseOrder.locationOfDelivery || '',
      paymentTerms: purchaseOrder.paymentTerms || '',
      termsOfPayment: purchaseOrder.termsOfPayment || '',
      terms: purchaseOrder.terms || '',
      specialInstructions: purchaseOrder.specialInstructions || '',
      remittanceInstructions: purchaseOrder.remittanceInstructions || '',
      currency: purchaseOrder.currency || 'USD',
      quotationCurrency: purchaseOrder.quotationCurrency || purchaseOrder.currency || 'USD',
      companyCurrencyCode: purchaseOrder.companyCurrencyCode || purchaseOrder.currency || 'USD',
      exchangeRate: purchaseOrder.exchangeRate != null ? String(purchaseOrder.exchangeRate) : '',
      exchangeRateDate: purchaseOrder.exchangeRateDate || '',
      exchangeRateDirect: Boolean(purchaseOrder.exchangeRateDirect),
      totalDiscount: purchaseOrder.totalDiscount != null ? String(purchaseOrder.totalDiscount) : '',
      freightCost: purchaseOrder.freightCost != null ? String(purchaseOrder.freightCost) : '',
      packagingCost: purchaseOrder.packagingCost != null ? String(purchaseOrder.packagingCost) : '',
      truckCost: purchaseOrder.truckCost != null ? String(purchaseOrder.truckCost) : '',
      bargeCost: purchaseOrder.bargeCost != null ? String(purchaseOrder.bargeCost) : '',
      otherCost: purchaseOrder.otherCost != null ? String(purchaseOrder.otherCost) : '',
      vatOnLogistics:
        purchaseOrder.vatOnLogistics != null ? String(purchaseOrder.vatOnLogistics) : '',
    })
    setLines(
      (requisition?.lines || []).map((line, index) => {
        const quotationLine = quotationLines[index]
        const draft = createEmptyDirectPoLine()
        return {
          ...draft,
          id: line.id,
          quantity: line.quantity != null ? String(line.quantity) : '1',
          unitOfMeasurement: quotationLine?.unitOfMeasurement || line.unitOfMeasurement || '',
          unitPrice: quotationLine?.unitPrice != null ? String(quotationLine.unitPrice) : '',
          assignedQuantity:
            quotationLine?.assignedQuantity != null ? String(quotationLine.assignedQuantity) : '',
          notes: line.notes || line.remarks || quotationLine?.remarks || '',
          logisticsAccountCodeId:
            quotationLine?.logisticsAccountCodeId != null
              ? String(quotationLine.logisticsAccountCodeId)
              : '',
          logisticsSubaccountCodeId:
            quotationLine?.logisticsSubaccountCodeId != null
              ? String(quotationLine.logisticsSubaccountCodeId)
              : '',
          budgetAccountCodeId:
            quotationLine?.budgetAccountCodeId != null
              ? String(quotationLine.budgetAccountCodeId)
              : '',
          budgetSubaccountCodeId:
            quotationLine?.budgetSubaccountCodeId != null
              ? String(quotationLine.budgetSubaccountCodeId)
              : '',
          supplyCapacity:
            quotationLine?.supplyCapacity != null ? String(quotationLine.supplyCapacity) : '',
          deliveryTimeDays:
            quotationLine?.deliveryTimeDays != null ? String(quotationLine.deliveryTimeDays) : '',
          lineFiles: [],
          existingAttachments: (line.attachments || []).map((attachment) => ({
            id: attachment.id,
            fileName: attachment.fileName,
          })),
          manualItemName:
            quotationLine?.manualItemName ||
            line.manualItemName ||
            line.partName ||
            line.part?.name ||
            '',
          itemSearchText:
            quotationLine?.manualItemName ||
            line.manualItemName ||
            line.partName ||
            line.part?.name ||
            '',
          inventoryItemId: quotationLine?.inventoryItemId ?? line.inventoryItemId ?? null,
          masterInventoryItemId: quotationLine?.masterInventoryItemId ?? line.masterItemId ?? null,
          robDisplay:
            line.part?.robQuantity != null
              ? String(line.part.robQuantity)
              : line.part?.rob != null
              ? String(line.part.rob)
              : '',
          equipmentId: line.subcomponent?.equipmentId
            ? String(line.subcomponent.equipmentId)
            : quotationLine?.subcomponent?.equipmentId
            ? String(quotationLine.subcomponent.equipmentId)
            : '',
          equipmentName:
            line.subcomponent?.equipmentName || quotationLine?.subcomponent?.equipmentName || '',
          componentId: line.subcomponentId
            ? String(line.subcomponentId)
            : quotationLine?.subcomponent?.id
            ? String(quotationLine.subcomponent.id)
            : '',
          componentName: line.subcomponent?.name || quotationLine?.subcomponent?.name || '',
          partId: line.partId
            ? String(line.partId)
            : quotationLine?.partId
            ? String(quotationLine.partId)
            : '',
          partName: line.part?.name || quotationLine?.part?.name || line.partName || '',
          serviceHeadId: line.inventoryItemHeadId
            ? String(line.inventoryItemHeadId)
            : requisition?.inventoryItemHeadId
            ? String(requisition.inventoryItemHeadId)
            : '',
          serviceSubHeadId: line.inventoryItemSubHeadId
            ? String(line.inventoryItemSubHeadId)
            : requisition?.inventoryItemSubHeadId
            ? String(requisition.inventoryItemSubHeadId)
            : '',
        }
      })
    )
    const savedCompanyId = toPositiveNumber(purchaseOrder.companyGroupAdmin?.id)
    if (inventoryScopeAll && savedCompanyId) {
      setSelectedCompanyId(savedCompanyId)
      setSelectedCompanyUsername(
        String((purchaseOrder.companyGroupAdmin as any)?.username || '').trim()
      )
    }
  }, [visible, purchaseOrder, currentUserSignature])

  useEffect(() => {
    if (!visible) return
    if (inventoryScopeAll) return
    const companyUsername = String(currentUser?.companyGroupAdmin?.username || '').trim()
    if (companyUsername) {
      setSelectedCompanyUsername(companyUsername)
    }
  }, [visible, inventoryScopeAll, currentCompanyUsername])

  useEffect(() => {
    if (!visible) return
    const loadScopedVessels = async () => {
      if (inventoryScopeAll) {
        try {
          if (selectedCompanyUsername) {
            setVessels(await getVesselsByCompany(selectedCompanyUsername))
            return
          }
          if (!selectedCompanyId) {
            setVessels([])
            return
          }
          const allVessels = await getAllVessels()
          setVessels(
            allVessels.filter(
              (vessel) =>
                vessel.companyGroupAdminId === selectedCompanyId ||
                vessel.companyGroupAdmin?.id === selectedCompanyId
            )
          )
        } catch (error: any) {
          setVessels([])
          toast.error(error?.message || 'Failed to load company vessels')
        }
        return
      }

      try {
        const companyUsername = String(
          selectedCompanyUsername || currentCompanyUsername || ''
        ).trim()
        if (companyUsername) {
          setVessels(await getVesselsByCompany(companyUsername))
          return
        }
        const allVessels = await getAllVessels()
        const scopedRows = currentCompanyGroupAdminId
          ? allVessels.filter(
              (vessel) =>
                vessel.companyGroupAdminId === currentCompanyGroupAdminId ||
                vessel.companyGroupAdmin?.id === currentCompanyGroupAdminId
            )
          : allVessels
        setVessels(scopedRows)
      } catch (error: any) {
        setVessels([])
        toast.error(error?.message || 'Failed to load vessels')
      }
    }

    void loadScopedVessels()
  }, [
    visible,
    inventoryScopeAll,
    selectedCompanyUsername,
    selectedCompanyId,
    currentCompanyUsername,
    currentCompanyGroupAdminId,
  ])

  useEffect(() => {
    if (!visible) return
    const loadCategories = async () => {
      try {
        if (!effectiveCompanyId) {
          setCategories([])
          return
        }
        if (inventoryScopeAll) {
          const vessel = vessels.find((row) => String(row.id) === form.vesselId)
          const rows = await getAllInventoryCategoriesForCompany(
            effectiveCompanyId,
            toPositiveNumber(form.vesselId) ?? undefined,
            typeof vessel?.vesselType === 'string' ? vessel.vesselType : undefined
          )
          setCategories(rows || [])
          return
        }

        const rows = effectiveCompanyId
          ? await getAllInventoryCategoriesForCompany(
              effectiveCompanyId,
              toPositiveNumber(form.vesselId) ?? undefined,
              typeof vessels.find((row) => String(row.id) === form.vesselId)?.vesselType ===
                'string'
                ? vessels.find((row) => String(row.id) === form.vesselId)?.vesselType
                : undefined
            )
          : []
        setCategories(rows || [])
      } catch (error: any) {
        setCategories([])
        toast.error(error?.message || 'Failed to load categories')
      }
    }
    void loadCategories()
  }, [visible, inventoryScopeAll, effectiveCompanyId, form.vesselId, vessels])

  useEffect(() => {
    const requestSequence = ++accountRequestSequence.current
    if (!visible || !effectiveCompanyId) {
      setAccounts([])
      setAccountsLoading(false)
      return
    }
    setAccountsLoading(true)
    getAccountingAccounts(effectiveCompanyId)
      .then((rows) => {
        if (requestSequence === accountRequestSequence.current) setAccounts(rows || [])
      })
      .catch(() => {
        if (requestSequence === accountRequestSequence.current) setAccounts([])
      })
      .finally(() => {
        if (requestSequence === accountRequestSequence.current) setAccountsLoading(false)
      })
  }, [visible, effectiveCompanyId])

  useEffect(() => {
    if (!visible || categoryKind !== 'SPARES' || !form.vesselId) {
      setEquipmentOptions([])
      setEquipmentStatus('idle')
      setEquipmentMessage(null)
      return
    }

    setEquipmentStatus('loading')
    setEquipmentMessage(null)
    void fetchEquipmentOptionsForVessel(Number(form.vesselId), (id) => getEquipmentList(id)).then(
      (result) => {
        setEquipmentOptions(
          result.options.map((option) => ({
            id: option.value,
            name: option.label,
          }))
        )
        setEquipmentStatus(result.status)
        setEquipmentMessage(result.message ?? null)
      }
    )
  }, [visible, categoryKind, form.vesselId])

  useEffect(() => {
    if (!visible || categoryKind !== 'SERVICES') {
      setServiceHeads([])
      setServiceSubheadsByLine({})
      return
    }

    void loadScopedInventoryHeads({
      currentUser,
      inventoryScopeAll,
      inventoryScopeCompany: !inventoryScopeAll,
      inventoryScopeVessel: true,
      isServicesCategory: true,
      selectedCompanyId,
      selectedVesselId: form.vesselId,
      selectedVesselName:
        vessels.find((row) => String(row.id) === form.vesselId)?.fleet_name ?? null,
      selectedCompanyName,
      debugContext: 'Direct PO Services',
      requireVesselWhenCompanySelected: inventoryScopeAll,
    })
      .then((result) => {
        setServiceHeads(result.rows || [])
      })
      .catch(() => setServiceHeads([]))
  }, [
    visible,
    categoryKind,
    currentUserSignature,
    inventoryScopeAll,
    selectedCompanyId,
    form.vesselId,
    vessels,
    selectedCompanyName,
  ])

  useEffect(() => {
    if (!visible || categoryKind !== 'SERVICES') return
    lines.forEach((line, lineIndex) => {
      if (!line.serviceHeadId || serviceSubheadsByLine[lineIndex]?.length) return
      void loadScopedInventorySubHeads({
        currentUser,
        inventoryScopeAll,
        inventoryScopeCompany: !inventoryScopeAll,
        inventoryScopeVessel: true,
        isServicesCategory: true,
        selectedCompanyId,
        selectedVesselId: form.vesselId,
        selectedHeadId: line.serviceHeadId,
        selectedVesselName:
          vessels.find((row) => String(row.id) === form.vesselId)?.fleet_name ?? null,
        selectedCompanyName,
        debugContext: 'Direct PO Services',
        requireVesselWhenCompanySelected: inventoryScopeAll,
      })
        .then((result) => {
          setServiceSubheadsByLine((prev) => ({...prev, [lineIndex]: result.rows || []}))
        })
        .catch(() => setServiceSubheadsByLine((prev) => ({...prev, [lineIndex]: []})))
    })
  }, [
    visible,
    categoryKind,
    lines,
    serviceSubheadsByLine,
    currentUserSignature,
    inventoryScopeAll,
    selectedCompanyId,
    form.vesselId,
    vessels,
    selectedCompanyName,
  ])

  useEffect(() => {
    setForm((prev) => ({...prev, requestFor: derivedRequestFor}))
  }, [derivedRequestFor])

  const updateForm = (patch: Partial<DirectPoFormDraft>) => {
    setForm((prev) => ({...prev, ...patch}))
  }

  const updateLine = (lineIndex: number, patch: Partial<DirectPoLineDraft>) => {
    setLines((prev) =>
      prev.map((line, index) => (index === lineIndex ? {...line, ...patch} : line))
    )
  }

  const resetDownstreamLineState = (nextKind: DirectPoCategoryKind = categoryKind) => {
    setLines((prev) =>
      prev.map((line) => ({
        ...resetLineForCategoryKind(line, nextKind),
        equipmentId: nextKind === 'SPARES' ? '' : '',
        equipmentName: '',
        componentId: '',
        componentName: '',
        partId: '',
        partName: '',
      }))
    )
    setStoreOptionsByLine({})
    setSubcomponentsByLine({})
    setSubcomponentStatusByLine({})
    setSubcomponentMessageByLine({})
    setPartsByLine({})
    setPartStatusByLine({})
    setPartMessageByLine({})
    setServiceSubheadsByLine({})
  }

  const handleCompanyChange = async (companyIdRaw: string) => {
    const companyId = toPositiveNumber(companyIdRaw)
    const company = companies.find((row) => row.id === companyId)
    setSelectedCompanyId(companyId)
    setSelectedCompanyUsername(String(company?.username || '').trim())
    setForm((prev) => ({
      ...prev,
      vesselId: '',
      categoryId: '',
      requestFor: 'TECHNICAL',
    }))
    setVessels([])
    setAccounts([])
    setAccountsLoading(false)
    setLines((prev) =>
      prev.map((line) => ({
        ...line,
        logisticsAccountCodeId: '',
        logisticsSubaccountCodeId: '',
        budgetAccountCodeId: '',
        budgetSubaccountCodeId: '',
      }))
    )
    setSubaccountsByLine({})
    setBudgetSubaccountsByLine({})
    setEquipmentOptions([])
    setEquipmentStatus('idle')
    setEquipmentMessage(null)
    resetDownstreamLineState('NONE')
  }

  const handleVesselChange = (nextVesselId: string) => {
    const selectedVessel = vessels.find((row) => String(row.id) === String(nextVesselId))
    const derivedCompanyId = toPositiveNumber(
      selectedVessel?.companyGroupAdminId ?? selectedVessel?.companyGroupAdmin?.id
    )
    const companyChangedForVendorScope =
      inventoryScopeAll && derivedCompanyId !== null && derivedCompanyId !== effectiveCompanyId
    const companyChangedForAccounting = companyChangedForVendorScope
    if (inventoryScopeAll && derivedCompanyId) {
      setSelectedCompanyId(derivedCompanyId)
      setSelectedCompanyUsername(String(selectedVessel?.companyGroupAdmin?.username || '').trim())
    }
    setForm((prev) => ({
      ...prev,
      vesselId: nextVesselId,
      vendorId: '',
      vendorCompanyMappingId: '',
      categoryId: '',
      requestFor: 'TECHNICAL',
    }))
    if (companyChangedForAccounting) {
      setAccounts([])
      setSubaccountsByLine({})
      setBudgetSubaccountsByLine({})
      setLines((prev) =>
        prev.map((line) => ({
          ...line,
          logisticsAccountCodeId: '',
          logisticsSubaccountCodeId: '',
          budgetAccountCodeId: '',
          budgetSubaccountCodeId: '',
        }))
      )
    }
    if (companyChangedForVendorScope) {
      setVendors([])
    }
    setEquipmentOptions([])
    setEquipmentStatus('idle')
    setEquipmentMessage(null)
    resetDownstreamLineState('NONE')
  }

  const handleCategoryChange = (nextCategoryId: string) => {
    const nextCategoryName = getCategoryNameById(categories, nextCategoryId)
    const nextKind = deriveDirectPoCategoryKind(nextCategoryName)
    if (hasLineData) {
      const confirmed = window.confirm(
        'Changing category will reset incompatible line item values. Continue?'
      )
      if (!confirmed) return
    }
    resetDownstreamLineState(nextKind)
    setEquipmentOptions([])
    setEquipmentStatus('idle')
    setEquipmentMessage(null)
    updateForm({
      categoryId: nextCategoryId,
      requestFor: deriveRequestForFromCategoryKind(nextKind),
    })
  }

  const loadStoreOptions = async (lineIndex: number, search: string) => {
    if (!form.vesselId) return
    try {
      const response = await getVesselInventoryProjection({
        vesselId: Number(form.vesselId),
        itemFor: 'GENERAL_STORES',
        search: search || undefined,
        inventoryItemSubCategoryId: lines[lineIndex]?.inventoryItemSubCategoryId ?? null,
        page: 0,
        size: 25,
      })
      setStoreOptionsByLine((prev) => ({...prev, [lineIndex]: response.content || []}))
    } catch {
      setStoreOptionsByLine((prev) => ({...prev, [lineIndex]: []}))
    }
  }

  const loadSubaccounts = async (lineIndex: number, accountCodeId: string) => {
    const requestKey = `logistics:${lineIndex}`
    const requestSequence = (subaccountRequestSequence.current[requestKey] || 0) + 1
    subaccountRequestSequence.current[requestKey] = requestSequence
    if (!accountCodeId) {
      setSubaccountsByLine((prev) => ({...prev, [lineIndex]: []}))
      return
    }
    try {
      const rows = await getSubAccounts(
        Number(accountCodeId),
        effectiveCompanyId ?? undefined,
        toPositiveNumber(form.vesselId) ?? undefined,
        typeof selectedVessel?.vesselType === 'string' ? selectedVessel.vesselType : undefined
      )
      if (subaccountRequestSequence.current[requestKey] === requestSequence) {
        setSubaccountsByLine((prev) => ({...prev, [lineIndex]: rows || []}))
      }
    } catch {
      if (subaccountRequestSequence.current[requestKey] === requestSequence) {
        setSubaccountsByLine((prev) => ({...prev, [lineIndex]: []}))
      }
    }
  }

  const loadBudgetSubaccounts = async (lineIndex: number, accountCodeId: string) => {
    const requestKey = `budget:${lineIndex}`
    const requestSequence = (subaccountRequestSequence.current[requestKey] || 0) + 1
    subaccountRequestSequence.current[requestKey] = requestSequence
    if (!accountCodeId) {
      setBudgetSubaccountsByLine((prev) => ({...prev, [lineIndex]: []}))
      return
    }
    try {
      const rows = await getSubAccounts(
        Number(accountCodeId),
        effectiveCompanyId ?? undefined,
        toPositiveNumber(form.vesselId) ?? undefined,
        typeof selectedVessel?.vesselType === 'string' ? selectedVessel.vesselType : undefined
      )
      if (subaccountRequestSequence.current[requestKey] === requestSequence) {
        setBudgetSubaccountsByLine((prev) => ({...prev, [lineIndex]: rows || []}))
      }
    } catch {
      if (subaccountRequestSequence.current[requestKey] === requestSequence) {
        setBudgetSubaccountsByLine((prev) => ({...prev, [lineIndex]: []}))
      }
    }
  }

  const loadSubcomponents = async (lineIndex: number, equipmentId: string) => {
    if (!equipmentId) {
      setSubcomponentsByLine((prev) => ({...prev, [lineIndex]: []}))
      setSubcomponentStatusByLine((prev) => ({...prev, [lineIndex]: 'idle'}))
      setSubcomponentMessageByLine((prev) => ({...prev, [lineIndex]: null}))
      setPartsByLine((prev) => ({...prev, [lineIndex]: []}))
      setPartStatusByLine((prev) => ({...prev, [lineIndex]: 'idle'}))
      setPartMessageByLine((prev) => ({...prev, [lineIndex]: null}))
      return
    }
    setSubcomponentStatusByLine((prev) => ({...prev, [lineIndex]: 'loading'}))
    setSubcomponentMessageByLine((prev) => ({...prev, [lineIndex]: null}))
    try {
      const rows = await getSubComponentsByEquipment(Number(equipmentId), true)
      setSubcomponentsByLine((prev) => ({...prev, [lineIndex]: rows || []}))
      setSubcomponentStatusByLine((prev) => ({
        ...prev,
        [lineIndex]: rows.length > 0 ? 'loaded' : 'empty',
      }))
      setSubcomponentMessageByLine((prev) => ({
        ...prev,
        [lineIndex]: rows.length > 0 ? null : 'No sub components found for selected equipment',
      }))
    } catch {
      setSubcomponentsByLine((prev) => ({...prev, [lineIndex]: []}))
      setSubcomponentStatusByLine((prev) => ({...prev, [lineIndex]: 'error'}))
      setSubcomponentMessageByLine((prev) => ({
        ...prev,
        [lineIndex]: 'Unable to load sub components',
      }))
    }
  }

  const loadParts = async (lineIndex: number, subcomponentId: string) => {
    if (!subcomponentId) {
      setPartsByLine((prev) => ({...prev, [lineIndex]: []}))
      setPartStatusByLine((prev) => ({...prev, [lineIndex]: 'idle'}))
      setPartMessageByLine((prev) => ({...prev, [lineIndex]: null}))
      return
    }
    setPartStatusByLine((prev) => ({...prev, [lineIndex]: 'loading'}))
    setPartMessageByLine((prev) => ({...prev, [lineIndex]: null}))
    try {
      const rows = await getPartsBySubcomponent(Number(subcomponentId))
      setPartsByLine((prev) => ({...prev, [lineIndex]: rows || []}))
      setPartStatusByLine((prev) => ({...prev, [lineIndex]: rows.length > 0 ? 'loaded' : 'empty'}))
      setPartMessageByLine((prev) => ({
        ...prev,
        [lineIndex]: rows.length > 0 ? null : 'No parts found for selected sub component',
      }))
    } catch {
      setPartsByLine((prev) => ({...prev, [lineIndex]: []}))
      setPartStatusByLine((prev) => ({...prev, [lineIndex]: 'error'}))
      setPartMessageByLine((prev) => ({...prev, [lineIndex]: 'Unable to load parts'}))
    }
  }

  // Edit hydration must load the option lists required to display saved IDs.
  // Create mode still uses the same handlers when the user changes a field.
  useEffect(() => {
    if (!visible || mode !== 'edit' || !purchaseOrder) return
    lines.forEach((line, lineIndex) => {
      if (line.logisticsAccountCodeId && subaccountsByLine[lineIndex] === undefined) {
        void loadSubaccounts(lineIndex, line.logisticsAccountCodeId)
      }
      if (line.budgetAccountCodeId && budgetSubaccountsByLine[lineIndex] === undefined) {
        void loadBudgetSubaccounts(lineIndex, line.budgetAccountCodeId)
      }
      if (
        categoryKind === 'GENERAL_STORES' &&
        line.masterInventoryItemId &&
        storeOptionsByLine[lineIndex] === undefined
      ) {
        void loadStoreOptions(lineIndex, line.itemSearchText)
      }
      if (
        categoryKind === 'SPARES' &&
        line.equipmentId &&
        (subcomponentStatusByLine[lineIndex] === undefined ||
          subcomponentStatusByLine[lineIndex] === 'idle')
      ) {
        void loadSubcomponents(lineIndex, line.equipmentId)
      }
      if (
        categoryKind === 'SPARES' &&
        line.componentId &&
        (partStatusByLine[lineIndex] === undefined || partStatusByLine[lineIndex] === 'idle')
      ) {
        void loadParts(lineIndex, line.componentId)
      }
    })
  }, [
    visible,
    mode,
    purchaseOrder?.id,
    lines,
    categoryKind,
    subaccountsByLine,
    budgetSubaccountsByLine,
    storeOptionsByLine,
    subcomponentStatusByLine,
    partStatusByLine,
  ])

  const loadServiceSubheads = async (lineIndex: number, headId: string) => {
    if (!headId) {
      setServiceSubheadsByLine((prev) => ({...prev, [lineIndex]: []}))
      return
    }
    try {
      const result = await loadScopedInventorySubHeads({
        currentUser,
        inventoryScopeAll,
        inventoryScopeCompany: !inventoryScopeAll,
        inventoryScopeVessel: true,
        isServicesCategory: true,
        selectedCompanyId,
        selectedVesselId: form.vesselId,
        selectedHeadId: headId,
        selectedVesselName:
          vessels.find((row) => String(row.id) === form.vesselId)?.fleet_name ?? null,
        selectedCompanyName,
        debugContext: 'Direct PO Services',
        requireVesselWhenCompanySelected: inventoryScopeAll,
      })
      setServiceSubheadsByLine((prev) => ({...prev, [lineIndex]: result.rows || []}))
    } catch {
      setServiceSubheadsByLine((prev) => ({...prev, [lineIndex]: []}))
    }
  }

  const addLine = () => setLines((prev) => [...prev, createEmptyDirectPoLine()])
  const removeLine = (lineIndex: number) => {
    setLines((prev) => prev.filter((_, index) => index !== lineIndex))
  }

  const validate = () => {
    const nextErrors: Record<string, string> = {}
    if (!form.vesselId) nextErrors.vesselId = 'Vessel is required'
    if (!form.vendorId) nextErrors.vendorId = 'Vendor is required'
    if (!form.requestedPortId) nextErrors.requestedPortId = 'Requested Port is required'
    if (!form.categoryId) nextErrors.categoryId = 'Category is required'
    if (!showLineSection) nextErrors.lines = 'Complete procurement context before adding line items'
    if (form.requiredDeliveryDate && form.requiredDeliveryDate < TODAY) {
      nextErrors.requiredDeliveryDate = 'Required Delivery Date cannot be in the past'
    }
    lines.forEach((line, index) => {
      if (!line.quantity || Number(line.quantity) <= 0) {
        nextErrors[`line-${index}-quantity`] = 'Quantity must be greater than zero'
      }
      if (!line.unitPrice || Number(line.unitPrice) <= 0) {
        nextErrors[`line-${index}-unitPrice`] = 'Unit price must be greater than zero'
      }
      if (!line.unitOfMeasurement.trim()) {
        nextErrors[`line-${index}-uom`] = 'UOM is required'
      }
      if (categoryKind === 'GENERAL_STORES') {
        if (!line.manualItemName.trim() && !line.masterInventoryItemId && !line.inventoryItemId) {
          nextErrors[`line-${index}-item`] = 'General stores item is required'
        }
      } else if (categoryKind === 'SPARES') {
        if (!line.equipmentId) nextErrors[`line-${index}-equipment`] = 'Equipment is required'
        if (!line.componentId) nextErrors[`line-${index}-component`] = 'Component is required'
        if (!line.partId && !line.manualItemName.trim()) {
          nextErrors[`line-${index}-part`] = 'Part or item name is required'
        }
      } else if (categoryKind === 'SERVICES') {
        if (!line.serviceHeadId) {
          nextErrors[`line-${index}-serviceHead`] = 'Head is required'
        }
        if (!line.serviceSubHeadId) {
          nextErrors[`line-${index}-serviceSubHead`] = 'Sub Head is required'
        }
        if (!line.manualItemName.trim()) {
          nextErrors[`line-${index}-service`] = 'Service scope is required'
        }
      }
    })
    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const hasFiles =
    requisitionFiles.length > 0 ||
    rfqFiles.length > 0 ||
    quotationFiles.length > 0 ||
    poFiles.length > 0 ||
    lines.some((line) => line.lineFiles.length > 0)

  const handleSubmit = async () => {
    if (!validate()) return
    setSubmitting(true)
    try {
      const payload = buildDirectPurchaseOrderPayload(form, lines, categoryKind)
      const files: DirectPurchaseOrderFiles | undefined = hasFiles
        ? {
            requisitionFiles,
            rfqFiles,
            quotationFiles,
            poFiles,
            lineFilesByIndex: lines.reduce<Record<number, File[]>>((acc, line, index) => {
              if (line.lineFiles.length > 0) acc[index] = line.lineFiles
              return acc
            }, {}),
          }
        : undefined
      const saved =
        mode === 'edit' && purchaseOrder?.id
          ? await updateDirectPurchaseOrder(purchaseOrder.id, payload, files)
          : await createDirectPurchaseOrder(payload, files)
      toast.success(
        mode === 'edit' ? 'Direct PO updated successfully' : 'Direct PO created successfully'
      )
      await onSuccess(saved)
      onClose()
    } catch (error: any) {
      toast.error(error?.message || 'Failed to save Direct PO')
    } finally {
      setSubmitting(false)
    }
  }

  if (!visible) return null

  return (
    <div
      className='modal fade show d-flex align-items-center justify-content-center'
      tabIndex={-1}
      style={{
        backgroundColor: 'rgba(0,0,0,0.5)',
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 1060,
      }}
    >
      <div className='modal-dialog modal-xl modal-dialog-centered' role='document'>
        <div className='modal-content bg-white'>
          <div className='modal-header'>
            <div>
              <h5 className='modal-title'>
                {mode === 'edit' ? 'Edit Direct PO' : 'Create Direct PO'}
              </h5>
              <div className='text-muted fs-7'>
                Progressive, category-driven procurement form for direct purchase orders.
              </div>
            </div>
            <button type='button' className='btn-close' onClick={onClose}></button>
          </div>

          <div className='modal-body' style={{maxHeight: '78vh', overflowY: 'auto'}}>
            {loading ? (
              <div className='text-muted'>Loading Direct PO form...</div>
            ) : (
              <>
                <ProcurementContextSection
                  inventoryScopeAll={inventoryScopeAll}
                  selectedCompanyId={selectedCompanyId}
                  selectedCompanyName={selectedCompanyName}
                  companies={companies}
                  vessels={vessels}
                  vendors={vendors}
                  ports={ports}
                  categories={categories}
                  form={form}
                  derivedRequestFor={derivedRequestFor}
                  errors={errors}
                  onCompanyChange={handleCompanyChange}
                  onVesselChange={handleVesselChange}
                  onVendorChange={(vendorId) => {
                    const vendor = vendors.find((row) => String(row.id) === vendorId)
                    updateForm({
                      vendorId,
                      vendorCompanyMappingId: vendor?.vendorCompanyMappingId
                        ? String(vendor.vendorCompanyMappingId)
                        : '',
                      paymentTerms: form.paymentTerms || vendor?.paymentTerms || '',
                      supplierReference:
                        form.supplierReference || vendor?.code || vendor?.vendorCode || '',
                    })
                  }}
                  onRequestedPortChange={(requestedPortId) => updateForm({requestedPortId})}
                  onDeliveryPortChange={(deliveryPortId) => updateForm({deliveryPortId})}
                  onCategoryChange={handleCategoryChange}
                />

                <BasicDetailsSection
                  today={TODAY}
                  form={form}
                  errors={errors}
                  onFormChange={updateForm}
                />

                {mode === 'create' && (
                  <div className='mb-4'>
                    <label className='form-label'>PO Number</label>
                    <input
                      aria-label='PO Number Preview'
                      className='form-control font-monospace'
                      readOnly
                      value={
                        poNumberPreviewStatus === 'loading'
                          ? 'Loading...'
                          : poNumberPreview || 'Unavailable until vessel and vendor are selected'
                      }
                    />
                    <small className='text-muted'>The final number is assigned authoritatively when the PO is created.</small>
                  </div>
                )}

                {showLineSection && (
                  <LineItemsSection
                    categoryKind={categoryKind}
                    vesselId={form.vesselId}
                    lines={lines}
                    errors={errors}
                    accounts={accounts}
                    accountsLoading={accountsLoading}
                    uoms={uoms}
                    subaccountsByLine={subaccountsByLine}
                    budgetSubaccountsByLine={budgetSubaccountsByLine}
                    storeOptionsByLine={storeOptionsByLine}
                    subCategories={subCategories}
                    equipmentOptions={equipmentOptions}
                    equipmentStatus={equipmentStatus}
                    equipmentMessage={equipmentMessage}
                    subcomponentsByLine={subcomponentsByLine}
                    subcomponentStatusByLine={subcomponentStatusByLine}
                    subcomponentMessageByLine={subcomponentMessageByLine}
                    partsByLine={partsByLine}
                    partStatusByLine={partStatusByLine}
                    partMessageByLine={partMessageByLine}
                    serviceHeads={serviceHeads}
                    serviceSubheadsByLine={serviceSubheadsByLine}
                    onAddLine={addLine}
                    onRemoveLine={removeLine}
                    onLineChange={updateLine}
                    onLoadStoreOptions={loadStoreOptions}
                    onLoadSubaccounts={loadSubaccounts}
                    onLoadBudgetSubaccounts={loadBudgetSubaccounts}
                    onLoadSubcomponents={loadSubcomponents}
                    onLoadParts={loadParts}
                    onLoadServiceSubheads={loadServiceSubheads}
                  />
                )}

                {showCommercialSection && (
                  <CommercialSection
                    form={form}
                    currencies={currencies}
                    currencyConversionRelevant={currencyConversionRelevant}
                    errors={errors}
                    onFormChange={updateForm}
                  />
                )}

                <AttachmentSection
                  filesByStage={[
                    ['Requisition Files', requisitionFiles, setRequisitionFiles],
                    ['RFQ Files', rfqFiles, setRfqFiles],
                    ['Quotation Files', quotationFiles, setQuotationFiles],
                    ['PO Files', poFiles, setPoFiles],
                  ]}
                />
              </>
            )}
          </div>

          <div className='modal-footer'>
            <button type='button' className='btn btn-light' onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button
              type='button'
              className='btn btn-primary'
              onClick={handleSubmit}
              disabled={loading || submitting}
            >
              {submitting ? 'Saving...' : mode === 'edit' ? 'Update Direct PO' : 'Create Direct PO'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DirectPurchaseOrderModal
